import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { StatCard, PageHeader, Loading, formatCurrency, BackButton } from '../components/Shared';
import { apiFetch } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// ── Section Satisfaction ────────────────────────────────────────────────────

const CURRENT_YEAR_SAT = new Date().getFullYear();
const YEAR_OPTS_SAT = [CURRENT_YEAR_SAT - 2, CURRENT_YEAR_SAT - 1, CURRENT_YEAR_SAT];

function SatPctBar({ score, max = 5 }) {
  if (score == null) return <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>;
  const pct = Math.round((score - 1) / (max - 1) * 100);
  const color = pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div style={{ flex: 1, background: '#e2e8f0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: '0.85rem', fontWeight: 700, color, minWidth: '36px', textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function SatisfactionSection() {
  const [annee, setAnnee] = useState(CURRENT_YEAR_SAT);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('gf_token');
    fetch(`${API_BASE}/dashboard/satisfaction?annee=${annee}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [annee]);

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open ? '1rem' : 0 }}>
        <button onClick={() => setOpen(o => !o)} style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          color: 'var(--text-primary)', fontWeight: 700, fontSize: '1rem',
        }}>
          <span style={{ transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>▶</span>
          Satisfaction formation
        </button>
        {open && (
          <select value={annee} onChange={e => setAnnee(Number(e.target.value))}
            style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', cursor: 'pointer' }}>
            {YEAR_OPTS_SAT.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>

      {open && (
        loading ? (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', padding: '0.5rem 0' }}>Chargement...</div>
        ) : !data ? null : (
          <div>
            {/* KPI annuels */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.7rem', marginBottom: '1.2rem' }}>
              {[
                { label: 'Stagiaires formés',    value: data.nb_stagiaires ?? '—' },
                { label: 'Sessions réalisées',   value: data.nb_sessions ?? '—' },
                { label: 'Taux de présence',     value: data.taux_presence != null ? `${data.taux_presence}%` : '—' },
                { label: 'Satisfaction globale', value: data.score_global != null ? `${Math.round((data.score_global - 1) / 4 * 100)}%` : '—' },
                { label: 'Réponses questionnaire stagiaires', value: data.nb_reponses_total ?? '—' },
                { label: 'Taux réponse client',  value: data.taux_reponse_client != null ? `${data.taux_reponse_client}%` : data.nb_envois_client > 0 ? '0%' : '—' },
              ].map(k => (
                <div key={k.label} style={{
                  background: 'var(--bg-elevated)', borderRadius: '8px',
                  padding: '0.6rem 0.8rem', border: '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{k.label}</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* Scores par indicateur */}
            {data.indicateurs && data.indicateurs.length > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
                  Scores par indicateur {annee}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {data.indicateurs.map(ind => (
                    <div key={ind.id} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 60px', alignItems: 'center', gap: '0.8rem' }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: ind.couleur, marginRight: '0.4rem', verticalAlign: 'middle' }} />
                        {ind.nom}
                      </div>
                      <SatPctBar score={ind.score_moyen} max={ind.score_max} />
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', textAlign: 'right' }}>
                        {ind.nb_reponses > 0 ? `${ind.nb_reponses} rép.` : '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.nb_reponses_total === 0 && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
                Aucune réponse enregistrée pour {annee}.
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
}

// ── Helpers statuts documents ────────────────────────────────────────────────

const DOCS_PRE = [
  { key: 'devis',      label: 'Devis' },
  { key: 'fiche',      label: 'Fiche insc.' },
  { key: 'convention', label: 'Convention' },
  { key: 'convoc',     label: 'Convocation' },
  { key: 'commande',   label: 'Cmd. formateur' },
];
const DOCS_IN = [
  { key: 'emarg',      label: 'Émargement' },
  { key: 'eval',       label: 'Éval.' },
  { key: 'fiche_prog', label: 'Fiche progrès' },
  { key: 'docs_envoi', label: 'Docs envoyés' },
];
const DOCS = [...DOCS_PRE, ...DOCS_IN];

function statutInfo(key, row) {
  if (key === 'devis') {
    const s = row.devis_statut;
    if (s === 'Validé')  return { color: '#16a34a', bg: '#f0fdf4', label: 'Validé' };
    if (s === 'Envoyé')  return { color: '#d97706', bg: '#fffbeb', label: 'Envoyé' };
    return { color: '#dc2626', bg: '#fef2f2', label: s || 'Non créé' };
  }
  if (key === 'fiche') {
    const s = row.fiche_statut;
    if (s === 'soumis')  return { color: '#16a34a', bg: '#f0fdf4', label: 'Soumis' };
    if (s === 'envoyé')  return { color: '#d97706', bg: '#fffbeb', label: 'Envoyé' };
    if (s === 'créé')    return { color: '#0284c7', bg: '#f0f9ff', label: 'Créé' };
    return { color: '#dc2626', bg: '#fef2f2', label: 'Non créé' };
  }
  if (key === 'convention') {
    const s = row.convention_statut;
    if (s === 'Validée') return { color: '#16a34a', bg: '#f0fdf4', label: 'Validée' };
    if (s === 'Envoyée' || s === 'Envoyé') return { color: '#d97706', bg: '#fffbeb', label: 'Envoyée' };
    return { color: '#dc2626', bg: '#fef2f2', label: s || 'Non créée' };
  }
  if (key === 'convoc') {
    const sent = row.convocation_envoyee;
    if (sent) return { color: '#16a34a', bg: '#f0fdf4', label: 'Envoyée' };
    return { color: '#dc2626', bg: '#fef2f2', label: 'Non envoyée' };
  }
  if (key === 'commande') {
    const s = row.commande_statut;
    if (s === 'Validé')  return { color: '#16a34a', bg: '#f0fdf4', label: 'Validé' };
    if (s === 'Envoyé')  return { color: '#d97706', bg: '#fffbeb', label: 'Envoyé' };
    return { color: '#dc2626', bg: '#fef2f2', label: s || 'Non créée' };
  }
  // ── Colonnes in-session (grises si session pas encore démarrée) ─────────────
  if (row.phase === 'a_venir') return { color: '#cbd5e1', bg: '#f8fafc', label: '—' };
  if (key === 'emarg') {
    const nb = Number(row.nb_emargements || 0);
    const clos = Number(row.nb_clos || 0);
    if (!nb) return { color: '#94a3b8', bg: '#f8fafc', label: 'Non créé' };
    const closJ = clos / 2;
    const totalJ = nb / 2;
    const lbl = `${closJ}/${totalJ} j`;
    if (clos === nb) return { color: '#16a34a', bg: '#f0fdf4', label: lbl };
    if (clos > 0)   return { color: '#d97706', bg: '#fffbeb', label: lbl };
    return { color: '#dc2626', bg: '#fef2f2', label: lbl };
  }
  if (key === 'eval') {
    const nb = Number(row.nb_evaluations || 0);
    if (!nb) return { color: '#dc2626', bg: '#fef2f2', label: 'Aucune' };
    return { color: '#16a34a', bg: '#f0fdf4', label: `${nb} rép.` };
  }
  if (key === 'fiche_prog') {
    const s = row.fiche_prog_statut;
    if (!s) return { color: '#dc2626', bg: '#fef2f2', label: 'Non créée' };
    if (row.fiche_prog_signee) return { color: '#16a34a', bg: '#f0fdf4', label: 'Signée' };
    return { color: '#d97706', bg: '#fffbeb', label: s };
  }
  if (key === 'docs_envoi') {
    const manquants = row.groupes_docs_manquants || [];
    const total = Number(row.nb_groupes_total || 0);
    const envoyes = Number(row.nb_groupes_envoyes || 0);
    if (!total) return { color: '#cbd5e1', bg: '#f8fafc', label: '—' };
    if (envoyes === total) return { color: '#16a34a', bg: '#f0fdf4', label: '✓' };
    const label = manquants.map(g => `[${g}]`).join('') + ' manquant';
    return { color: '#d97706', bg: '#fffbeb', label };
  }
  if (key === 'cloture') {
    if (row.cloture_formateur_at) return { color: '#16a34a', bg: '#f0fdf4', label: 'Clôturé' };
    return { color: '#dc2626', bg: '#fef2f2', label: 'En attente' };
  }
  if (key === 'prefact') {
    const nb = Number(row.nb_prefactures || 0);
    if (!nb) return { color: '#dc2626', bg: '#fef2f2', label: 'Non créée' };
    return { color: '#16a34a', bg: '#f0fdf4', label: `${nb} pf.` };
  }
  return { color: '#94a3b8', bg: '#f8fafc', label: '—' };
}

function StatutBadge({ docKey, row, small }) {
  const { color, bg, label } = statutInfo(docKey, row);
  return (
    <span style={{
      display: 'inline-block',
      padding: small ? '0.1rem 0.45rem' : '0.18rem 0.55rem',
      borderRadius: '6px',
      background: bg,
      color,
      border: `1px solid ${color}40`,
      fontSize: small ? '0.67rem' : '0.72rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  const [y, m, j] = d.split('-');
  return `${j}/${m}/${y}`;
}

// ── Tableau d'avancement des sessions ────────────────────────────────────────

function PhaseBadge({ phase }) {
  const cfg = {
    en_cours: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', dot: '#22c55e', label: 'En cours', pulse: true },
    a_venir:  { color: '#0369a1', bg: '#f0f9ff', border: '#bae6fd', dot: '#38bdf8', label: 'À venir',  pulse: false },
    termine:  { color: '#92400e', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', label: 'Terminé',  pulse: false },
    cloture:  { color: '#4b5563', bg: '#f1f5f9', border: '#cbd5e1', dot: '#94a3b8', label: 'Clôturé',  pulse: false },
  };
  const { color, bg, border, dot, label, pulse } = cfg[phase] || cfg['a_venir'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.68rem', fontWeight: 700, color, background: bg, border: `1px solid ${border}`, borderRadius: '4px', padding: '0.1rem 0.4rem', whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block', ...(pulse ? { boxShadow: '0 0 0 2px #bbf7d040' } : {}) }} />
      {label}
    </span>
  );
}

function AvancementSessions() {
  const { data, loading } = useApi('/dashboard/avancement');
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const sessions = data?.data || [];
  const nbEnCours = sessions.filter(s => s.est_en_cours).length;

  function toggle(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  const thStyle = { padding: '0.4rem 0.55rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', textAlign: 'center' };
  const thGroupStyle = { padding: '0.35rem 0.6rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', textAlign: 'center' };
  const tdStyle = { padding: '0.5rem 0.55rem', fontSize: '0.8rem', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', textAlign: 'center' };
  const tdLeftStyle = { ...tdStyle, textAlign: 'left' };

  return (
    <div style={{ marginBottom: '1.5rem', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ padding: '0.8rem 1rem', borderBottom: open ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>État d'avancement des sessions</span>
        {!loading && nbEnCours > 0 && (
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#15803d', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '0.05rem 0.5rem' }}>
            {nbEnCours} en cours
          </span>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          {loading ? '…' : `${sessions.length} session${sessions.length > 1 ? 's' : ''}`}
        </span>
      </div>
      {open && (loading ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Chargement...</div>
      ) : !sessions.length ? (
        <div style={{ padding: '1rem 0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem', fontStyle: 'italic' }}>Aucune session active ou à venir.</div>
      ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
          <thead>
            {/* Ligne 1 : groupes de colonnes */}
            <tr>
              <th colSpan={5} style={{ ...thGroupStyle, borderRight: '2px solid var(--border)' }}></th>
              <th colSpan={DOCS_PRE.length} style={{ ...thGroupStyle, color: '#0369a1', borderRight: '2px solid #bae6fd' }}>Pré-session</th>
              <th colSpan={DOCS_IN.length} style={{ ...thGroupStyle, color: '#7c3aed' }}>En session / Clôture</th>
            </tr>
            {/* Ligne 2 : en-têtes de colonnes */}
            <tr>
              <th style={{ ...thStyle, width: 28 }}></th>
              <th style={{ ...thStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>Session</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Formation</th>
              <th style={{ ...thStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>Démarrage</th>
              <th style={{ ...thStyle, borderRight: '2px solid var(--border)' }}>Phase</th>
              {DOCS_PRE.map((d, i) => <th key={d.key} style={{ ...thStyle, borderRight: i === DOCS_PRE.length - 1 ? '2px solid #bae6fd' : undefined }}>{d.label}</th>)}
              {DOCS_IN.map(d => <th key={d.key} style={thStyle}>{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {sessions.map(s => {
              const hasGroupes = s.groupes?.length > 0;
              const isOpen = expanded[s.session_id];
              const rowBg = s.est_en_cours ? 'rgba(240,253,244,0.35)' : undefined;
              return (
                <React.Fragment key={s.session_id}>
                  <tr
                    style={{ cursor: 'pointer', background: isOpen ? 'var(--bg-elevated)' : rowBg, transition: 'background 0.15s', borderLeft: s.est_en_cours ? '3px solid #22c55e' : '3px solid transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                    onMouseLeave={e => e.currentTarget.style.background = isOpen ? 'var(--bg-elevated)' : (rowBg || '')}
                  >
                    <td style={{ ...tdStyle, width: 28, padding: '0.5rem 0.3rem 0.5rem 0.5rem' }}>
                      {hasGroupes && (
                        <button onClick={e => { e.stopPropagation(); toggle(s.session_id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.72rem', color: 'var(--text-secondary)', padding: '0 4px', transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</button>
                      )}
                    </td>
                    <td style={{ ...tdLeftStyle, fontWeight: 700, color: 'var(--accent-blue)', fontSize: '0.78rem', whiteSpace: 'nowrap' }} onClick={() => navigate('/sessions/' + s.session_id)}>{s.session_id}</td>
                    <td style={{ ...tdLeftStyle, maxWidth: 200 }} onClick={() => navigate('/sessions/' + s.session_id)}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200, fontSize: '0.8rem', color: 'var(--text-primary)' }} title={s.intitule_formation}>{s.intitule_formation}</span>
                    </td>
                    <td style={{ ...tdLeftStyle, whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.78rem' }} onClick={() => navigate('/sessions/' + s.session_id)}>{formatDate(s.date_debut)}</td>
                    <td style={{ ...tdStyle, borderRight: '2px solid var(--border)' }} onClick={() => navigate('/sessions/' + s.session_id)}>
                      <PhaseBadge phase={s.phase} />
                    </td>
                    {DOCS_PRE.map((d, i) => (
                      <td key={d.key} style={{ ...tdStyle, borderRight: i === DOCS_PRE.length - 1 ? '2px solid #bae6fd' : undefined }} onClick={() => navigate('/sessions/' + s.session_id)}>
                        <StatutBadge docKey={d.key} row={s} />
                      </td>
                    ))}
                    {DOCS_IN.map(d => (
                      <td key={d.key} style={tdStyle} onClick={() => navigate('/sessions/' + s.session_id)}>
                        {s.phase !== 'a_venir'
                          ? <StatutBadge docKey={d.key} row={s} />
                          : <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>—</span>}
                      </td>
                    ))}
                  </tr>
                  {isOpen && s.groupes.map(g => (
                    <tr key={g.sous_session_id} style={{ background: 'var(--bg-elevated)', borderLeft: s.phase === 'en_cours' ? '3px solid #86efac' : '3px solid transparent' }}>
                      <td style={{ ...tdStyle, borderBottom: '1px solid var(--border)' }}></td>
                      <td style={{ ...tdLeftStyle, borderBottom: '1px solid var(--border)', paddingLeft: '1.2rem' }}>
                        <span style={{ display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', padding: '0.1rem 0.45rem', background: '#e2e8f0', borderRadius: '4px' }}>Gr. {g.groupe}</span>
                      </td>
                      <td style={{ ...tdLeftStyle, borderBottom: '1px solid var(--border)', fontSize: '0.76rem', color: '#475569' }} colSpan={2}>
                        {g.nom_client || '—'}
                        {g.nb_stagiaires ? <span style={{ marginLeft: 6, color: '#94a3b8', fontSize: '0.7rem' }}>({g.nb_stagiaires} stg.)</span> : null}
                      </td>
                      <td style={{ ...tdStyle, borderBottom: '1px solid var(--border)', borderRight: '2px solid var(--border)' }}></td>
                      {DOCS_PRE.map((d, i) => (
                        <td key={d.key} style={{ ...tdStyle, borderBottom: '1px solid var(--border)', borderRight: i === DOCS_PRE.length - 1 ? '2px solid #bae6fd' : undefined }}>
                          <StatutBadge docKey={d.key} row={g} small />
                        </td>
                      ))}
                      {DOCS_IN.map(d => (
                        <td key={d.key} style={{ ...tdStyle, borderBottom: '1px solid var(--border)' }}>
                          <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>—</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      ))}
    </div>
  );
}

// ── Graphique multi-lignes SVG ────────────────────────────────────────────────

const YEAR_COLORS = ['#94a3b8', '#f59e0b', '#3b82f6'];
const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const CURRENT_MONTH = new Date().getMonth(); // 0-indexé : 0=Jan … 11=Déc

function LineChart({ mensuel, years, valueKey, valueKeyEstime, formatVal, formatLabel }) {
  const W = 460, H = 195;
  const PAD = { top: 16, right: 12, bottom: 62, left: 46 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const series = years.map(y =>
    Array.from({ length: 12 }, (_, i) => {
      const found = mensuel.find(d => d.annee === y && d.mois === i + 1);
      return found ? Number(found[valueKey]) : 0;
    })
  );

  // Mask pointillé par année/mois : mois avec CA estimé OU mois futurs de l'année courante
  const dashMasks = years.map((y, si) =>
    Array.from({ length: 12 }, (_, i) => {
      if (si === 2 && i > CURRENT_MONTH) return true;
      if (!valueKeyEstime) return false;
      const found = mensuel.find(d => d.annee === y && d.mois === i + 1);
      return found ? Number(found[valueKeyEstime]) > 0 : false;
    })
  );

  const maxVal = Math.max(...series.flat(), 1);
  function px(i) { return PAD.left + (i / 11) * cW; }
  function py(v) { return PAD.top + cH - (v / maxVal) * cH; }

  // Découpe une série en segments plein/pointillé selon dashMask
  function buildSegs(vals, mask) {
    const segs = [];
    let segDashed = mask[0];
    let segPts = [[px(0).toFixed(1), py(vals[0]).toFixed(1)]];
    for (let i = 1; i < 12; i++) {
      const nowDashed = mask[i];
      if (nowDashed !== segDashed) {
        segPts.push([px(i).toFixed(1), py(vals[i]).toFixed(1)]);
        segs.push({ pts: segPts, dashed: segDashed });
        segPts = [[px(i).toFixed(1), py(vals[i]).toFixed(1)]];
        segDashed = nowDashed;
      } else {
        segPts.push([px(i).toFixed(1), py(vals[i]).toFixed(1)]);
      }
    }
    segs.push({ pts: segPts, dashed: segDashed });
    return segs.map(s => ({
      d: s.pts.map(([x, y], k) => `${k === 0 ? 'M' : 'L'}${x},${y}`).join(' '),
      dashed: s.dashed,
    }));
  }

  const gridVals = [0, 0.25, 0.5, 0.75, 1];
  const yMonth = PAD.top + cH + 14;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* Grille horizontale */}
      {gridVals.map((t, i) => (
        <line key={i}
          x1={PAD.left} y1={PAD.top + cH * (1 - t)}
          x2={W - PAD.right} y2={PAD.top + cH * (1 - t)}
          stroke="var(--border)" strokeWidth="1" />
      ))}

      {/* Courbes : segments plein (confirmé) et pointillé (estimé ou futur) */}
      {series.map((vals, si) => {
        const segs = buildSegs(vals, dashMasks[si]);
        const isCurrentYear = si === 2;
        const baseOpacity = si === 0 ? 0.6 : si === 1 ? 0.85 : 1;
        const baseWidth   = isCurrentYear ? 2.5 : 1.8;
        return segs.map((seg, ki) => (
          <path key={`seg-${si}-${ki}`}
            d={seg.d}
            fill="none"
            stroke={YEAR_COLORS[si]}
            strokeWidth={seg.dashed ? (isCurrentYear ? 2 : 1.5) : baseWidth}
            strokeDasharray={seg.dashed ? '5,4' : undefined}
            opacity={seg.dashed ? baseOpacity * 0.65 : baseOpacity} />
        ));
      })}

      {/* Points (mois avec valeur > 0 ; futur de l'année courante : cercle creux) */}
      {series.map((vals, si) =>
        vals.map((v, i) => {
          if (v === 0) return null;
          const isFuture = si === 2 && i > CURRENT_MONTH;
          const isDashed = dashMasks[si][i];
          const r = si === 2 ? 3.5 : 2.5;
          const op = si === 0 ? 0.65 : si === 1 ? 0.9 : 1;
          return (
            <circle key={`pt-${si}-${i}`}
              cx={px(i)} cy={py(v)} r={r}
              fill={isDashed ? 'var(--bg-primary)' : YEAR_COLORS[si]}
              stroke={isDashed ? YEAR_COLORS[si] : 'none'}
              strokeWidth={isDashed ? 1.5 : 0}
              opacity={isFuture ? op * 0.7 : op} />
          );
        })
      )}

      {/* Labels mois */}
      {MONTHS_SHORT.map((m, i) => (
        <text key={i} x={px(i)} y={yMonth} textAnchor="middle" fontSize={9} fill="var(--text-secondary)">{m}</text>
      ))}

      {/* Année : une fois à gauche par série */}
      {series.map((_, si) => (
        <text key={`ylbl-${si}`}
          x={2} y={yMonth + 13 + si * 11}
          textAnchor="start" fontSize={7.5} fontWeight={600}
          fill={YEAR_COLORS[si]}
          opacity={si === 0 ? 0.7 : si === 1 ? 0.9 : 1}>
          {years[si]}
        </text>
      ))}

      {/* Valeurs sous chaque mois pour les 3 années */}
      {series.map((vals, si) =>
        vals.map((v, i) => {
          if (v === 0) return null;
          const label = formatLabel ? formatLabel(v) : Math.round(v);
          return (
            <text key={`lbl-${si}-${i}`}
              x={px(i)} y={yMonth + 13 + si * 11}
              textAnchor="middle" fontSize={7.5}
              fill={YEAR_COLORS[si]}
              opacity={si === 0 ? 0.7 : si === 1 ? 0.9 : 1}>
              {label}
            </text>
          );
        })
      )}

      {/* Labels axe Y */}
      {[0, 0.5, 1].map((t, i) => {
        const v = maxVal * t;
        return (
          <text key={i} x={PAD.left - 5} y={PAD.top + cH * (1 - t) + 3.5}
            textAnchor="end" fontSize={9} fill="var(--text-secondary)">
            {formatVal ? formatVal(v) : Math.round(v)}
          </text>
        );
      })}
    </svg>
  );
}

function ChartLegend({ years }) {
  return (
    <div style={{ display: 'flex', gap: '1.2rem', justifyContent: 'center', marginTop: '0.3rem' }}>
      {years.map((y, i) => (
        <div key={y} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          <svg width="28" height="10">
            <line x1="0" y1="5" x2={i === 2 ? 14 : 28} y2="5"
              stroke={YEAR_COLORS[i]} strokeWidth={i === 2 ? 2.5 : 1.8}
              opacity={i === 0 ? 0.6 : i === 1 ? 0.85 : 1} />
            {i === 2 && <line x1="14" y1="5" x2="28" y2="5"
              stroke={YEAR_COLORS[i]} strokeWidth={2} strokeDasharray="4,3" opacity={0.6} />}
            <circle cx={i === 2 ? 11 : 14} cy="5" r="2.5" fill={YEAR_COLORS[i]}
              opacity={i === 0 ? 0.6 : i === 1 ? 0.85 : 1} />
          </svg>
          {y}
        </div>
      ))}
    </div>
  );
}

// ── Sessions à facturer ───────────────────────────────────────────────────────

const API_BASE_AF = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

function AFacturerSessions() {
  const { data, loading, error, reload } = useApi('/dashboard/a-facturer');
  const navigate = useNavigate();
  const { token } = useAuth();
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);

  const sessions = data?.data || [];

  function toggle(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  async function patchIgnorer(url) {
    try {
      await fetch(`${API_BASE_AF}${url}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ignoree: true }),
      });
      reload();
    } catch {}
  }

  const thS = {
    padding: '0.45rem 0.6rem', fontSize: '0.72rem', fontWeight: 700,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
    background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  };
  const tdS = { padding: '0.5rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

  function retardStyle(jours) {
    if (jours <= 14) return { color: '#f59e0b' };
    if (jours <= 60) return { color: '#ef4444', fontWeight: 600 };
    return { color: '#dc2626', fontWeight: 700 };
  }

  const NCOLS = 9; // nombre total de colonnes

  return (
    <div style={{ marginBottom: '1.5rem', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ padding: '0.8rem 1rem', borderBottom: open ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Sessions à facturer</span>
        {sessions.length > 0 && (
          <span style={{ background: '#ef4444', color: 'white', borderRadius: '12px', padding: '0.1rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>
            {sessions.length}
          </span>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          Sessions terminées · balance > 0 (devis validé − préfactures exportées)
        </span>
      </div>

      {open && (loading ? (
        <div style={{ padding: '1rem 1.2rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Chargement...</div>
      ) : error ? (
        <div style={{ padding: '1rem 1.2rem', color: '#ef4444', fontSize: '0.82rem', fontFamily: 'monospace' }}>Erreur : {error}</div>
      ) : sessions.length === 0 ? (
        <div style={{ padding: '1rem 1.2rem', color: '#16a34a', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          ✓ Toutes les sessions passées ont été facturées.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
            <thead>
              <tr>
                <th style={{ ...thS, width: 28 }}></th>
                <th style={thS}>Session</th>
                <th style={thS}>Formation</th>
                <th style={thS}>Démarrage</th>
                <th style={thS}>Fin</th>
                <th style={{ ...thS, textAlign: 'center' }}>Retard</th>
                <th style={{ ...thS, textAlign: 'center' }}>Stag.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Devis / Solde</th>
                <th style={{ ...thS, textAlign: 'center' }} title="Exclure de la facturation (absence justifiée, raison commerciale...)">Non fact.</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const groupes = Array.isArray(s.groupes) ? s.groupes : [];
                const hasGroupes = s.has_groupes;
                const isOpen = expanded[s.session_id];
                return (
                  <React.Fragment key={s.session_id}>
                    {/* Ligne session */}
                    <tr
                      style={{ cursor: 'pointer', background: isOpen ? 'var(--bg-elevated)' : undefined }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isOpen ? 'var(--bg-elevated)' : ''; }}>
                      <td style={{ ...tdS, width: 28, padding: '0.5rem 0.3rem 0.5rem 0.7rem' }}>
                        {hasGroupes && (
                          <button onClick={e => { e.stopPropagation(); toggle(s.session_id); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', color: 'var(--text-secondary)', padding: '0 4px', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▶</button>
                        )}
                      </td>
                      <td style={{ ...tdS, fontWeight: 700, color: 'var(--accent)', fontSize: '0.8rem', fontFamily: 'monospace' }}
                        onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        {s.session_id}
                      </td>
                      <td style={{ ...tdS, maxWidth: 220 }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, fontWeight: 600 }} title={s.intitule_formation}>
                          {s.intitule_formation}
                        </span>
                      </td>
                      <td style={{ ...tdS, whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.8rem' }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        {s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ ...tdS, whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.8rem' }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        {s.date_fin ? new Date(s.date_fin).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ ...tdS, textAlign: 'center' }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        <span style={{ fontSize: '0.78rem', ...retardStyle(s.jours_retard) }}>{s.jours_retard}j</span>
                      </td>
                      <td style={{ ...tdS, textAlign: 'center', color: 'var(--text-secondary)' }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        {s.nb_stagiaires}
                      </td>
                      <td style={{ ...tdS, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                        {Number(s.montant_ht) > 0 ? (
                          <span>
                            <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.76rem' }}>
                              {formatCurrency(s.montant_ht)}
                            </span>
                            <br />
                            <span style={{ color: s.solde_restant < s.montant_ht ? '#f59e0b' : 'var(--text-primary)', fontWeight: 700, fontSize: '0.85rem' }}>
                              {formatCurrency(s.solde_restant)}
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '0.78rem' }}>—</span>
                        )}
                      </td>
                      {/* Checkbox session uniquement si pas de groupes */}
                      <td style={{ ...tdS, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        {!hasGroupes && (
                          <input type="checkbox"
                            title="Exclure cette session de la facturation (absence, raison commerciale...)"
                            onChange={() => patchIgnorer(`/sessions/${s.session_id}/facturation-ignoree`)}
                            style={{ cursor: 'pointer', width: 15, height: 15 }} />
                        )}
                      </td>
                    </tr>

                    {/* Lignes groupes (dépliées) */}
                    {isOpen && groupes.map(g => (
                      <tr key={g.sous_session_id} style={{ background: 'var(--bg-elevated)' }}>
                        <td style={{ ...tdS, borderBottom: '1px solid var(--border)', padding: '0.35rem 0.3rem' }}></td>
                        <td style={{ ...tdS, borderBottom: '1px solid var(--border)', paddingLeft: '1.4rem' }}>
                          <span style={{ display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', padding: '0.1rem 0.45rem', background: '#e2e8f0', borderRadius: '4px' }}>
                            Gr. {g.groupe}
                          </span>
                        </td>
                        <td style={{ ...tdS, borderBottom: '1px solid var(--border)', fontSize: '0.78rem', color: 'var(--text-primary)' }} colSpan={5}>
                          {g.nom_client || '—'}
                          {g.nb_stagiaires ? <span style={{ marginLeft: 6, color: 'var(--text-tertiary)', fontSize: '0.72rem' }}>({g.nb_stagiaires} stg.)</span> : null}
                          {g.heures_absence > 0 ? (
                            <span style={{ marginLeft: 10, fontSize: '0.72rem', color: '#f59e0b', fontWeight: 600 }} title="Heures d'absence enregistrées">
                              {Number(g.heures_absence).toFixed(1)}h abs.
                            </span>
                          ) : null}
                        </td>
                        <td style={{ ...tdS, borderBottom: '1px solid var(--border)' }}></td>
                        <td style={{ ...tdS, borderBottom: '1px solid var(--border)', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                            title={`Exclure le groupe ${g.groupe} — ${g.nom_client || ''} de la facturation`}
                            onChange={() => patchIgnorer(`/session-groupes/${g.sous_session_id}/facturation-ignoree`)}
                            style={{ cursor: 'pointer', width: 15, height: 15 }} />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ── Tableau comparatif 3 ans ──────────────────────────────────────────────────

function evol(prev, curr) {
  if (!prev) return null;
  const p = ((curr - prev) / prev) * 100;
  const sign = p >= 0 ? '+' : '';
  const color = p >= 0 ? '#16a34a' : '#dc2626';
  return <span style={{ color, fontWeight: 700, fontSize: '0.8rem' }}>{sign}{p.toFixed(0)}%</span>;
}

function ComparatifTable({ comparatif }) {
  if (!comparatif?.length) return null;
  const [a, b, c] = comparatif;

  const rows = [
    { label: 'Sessions', key: 'sessions', fmt: v => v },
    { label: 'Stagiaires', key: 'stagiaires', fmt: v => v },
    { label: 'CA (HT)', key: 'ca_total', fmt: v => formatCurrency(v) },
  ];

  const thS = { padding: '0.5rem 0.8rem', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', textAlign: 'right' };
  const tdS = { padding: '0.55rem 0.8rem', fontSize: '0.88rem', borderBottom: '1px solid var(--border)', textAlign: 'right' };

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '1.5rem' }}>
      <div style={{ padding: '0.8rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
        Comparaison {a?.annee} — {b?.annee} — {c?.annee}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <caption style={{ captionSide: 'bottom', padding: '0.4rem 1rem', fontSize: '0.72rem', color: 'var(--text-tertiary)', textAlign: 'left' }}>
            * Année en cours — données partielles
          </caption>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: 'left', width: '30%' }}></th>
              <th style={thS}>{a?.annee}</th>
              <th style={thS}>{b?.annee}</th>
              <th style={{ ...thS, color: 'var(--text-primary)' }}>Évol. {a?.annee}→{b?.annee}</th>
              <th style={thS}>{c?.annee} *</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td style={{ ...tdS, textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)' }}>{r.label}</td>
                <td style={{ ...tdS, color: 'var(--text-secondary)' }}>{r.fmt(a?.[r.key] ?? 0)}</td>
                <td style={{ ...tdS, color: 'var(--text-secondary)' }}>{r.fmt(b?.[r.key] ?? 0)}</td>
                <td style={tdS}>{evol(a?.[r.key], b?.[r.key])}</td>
                <td style={{ ...tdS, fontWeight: 700, color: 'var(--text-primary)' }}>{r.fmt(c?.[r.key] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Section Recyclages ───────────────────────────────────────────────────────

function RecyclagesSection() {
  const navigate = useNavigate();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState(false);
  const [jours, setJours]     = useState(90);
  const [confirmIgnore, setConfirmIgnore] = useState(null); // { stagiaire_id, formation_id, nom, formation }

  const charger = (j) => {
    setLoading(true);
    apiFetch(`/dashboard/recyclages?jours=${j}`)
      .then(d => { setData(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { charger(jours); }, [jours]);

  const obligatoires = data.filter(r => r.recyclage === true);
  const conseilles   = data.filter(r => !r.recyclage);
  const enRetard     = data.filter(r => r.jours_restants < 0).length;
  const urgents      = data.filter(r => r.jours_restants >= 0 && r.jours_restants <= 30).length;
  const total        = data.length;

  function badgeJours(jours_restants) {
    if (jours_restants < 0)   return { label: 'Dépassé', bg: '#dc2626', color: '#fff' };
    if (jours_restants <= 30) return { label: 'Urgent',  bg: '#ea580c', color: '#fff' };
    if (jours_restants <= 60) return { label: 'À venir', bg: '#d97706', color: '#fff' };
    return { label: 'À venir', bg: '#64748b', color: '#fff' };
  }

  function joursLabel(j) {
    if (j < 0) return `${Math.abs(j)}j retard`;
    return `${j}j`;
  }

  const handleIgnorer = async () => {
    if (!confirmIgnore) return;
    try {
      await apiFetch('/dashboard/recyclages/ignorer', { method: 'POST', body: JSON.stringify({ stagiaire_id: confirmIgnore.stagiaire_id, formation_id: confirmIgnore.formation_id }) });
      setConfirmIgnore(null);
      charger(jours);
    } catch(e) { alert('Erreur : ' + e.message); }
  };

  function RecyclageTable({ rows }) {
    if (!rows.length) return <div style={{ color: 'var(--text-tertiary)', fontSize: '0.83rem', padding: '0.5rem 0' }}>Aucun élément.</div>;
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Stagiaire', 'Employeur', 'Formation', 'Dernière sess.', 'Échéance', 'Jours', 'Dernière relance', ''].map(h => (
                <th key={h} style={{ padding: '0.3rem 0.5rem', textAlign: 'left', fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const badge = badgeJours(r.jours_restants);
              return (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, whiteSpace: 'nowrap', cursor: r.stagiaire_id ? 'pointer' : 'default', color: 'var(--accent-blue)' }}
                    onClick={() => r.stagiaire_id && navigate(`/stagiaires/${r.stagiaire_id}`)}>
                    {r.nom_stagiaire || '—'}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.employeur || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.recyclage_formation_intitule || r.formation}>{r.recyclage_formation_intitule || r.formation || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {r.derniere_formation ? new Date(r.derniere_formation).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, whiteSpace: 'nowrap', color: badge.bg }}>
                    {r.date_echeance ? new Date(r.date_echeance).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>
                    {r.prochaine_session_date ? (
                      <span style={{ background: '#16a34a', color: '#fff', borderRadius: '10px', padding: '0.1rem 0.5rem', fontSize: '0.72rem', fontWeight: 700 }} title={r.prochaine_session_numero ? `Session ${r.prochaine_session_numero}` : undefined}>
                        Inscrit — {new Date(r.prochaine_session_date).toLocaleDateString('fr-FR')}
                      </span>
                    ) : (
                      <span style={{ background: badge.bg, color: badge.color, borderRadius: '10px', padding: '0.1rem 0.5rem', fontSize: '0.72rem', fontWeight: 700 }}>
                        {joursLabel(r.jours_restants)}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-tertiary)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {r.derniere_relance ? new Date(r.derniere_relance).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => setConfirmIgnore({ stagiaire_id: r.stagiaire_id, formation_id: r.formation_id, nom: r.nom_stagiaire, formation: r.formation })}
                      title="Ignorer définitivement ce recyclage"
                      style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '5px', padding: '0.15rem 0.45rem', fontSize: '0.72rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                      Ignorer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      {/* Modal confirmation ignorer */}
      {confirmIgnore && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem', maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.8rem' }}>Ignorer définitivement ce recyclage ?</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginBottom: '1.2rem', lineHeight: 1.5 }}>
              <strong>{confirmIgnore.nom}</strong><br/>
              {confirmIgnore.formation}<br/>
              Ce recyclage ne sera plus affiché dans le tableau de bord.
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmIgnore(null)} style={{ padding: '0.4rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', fontSize: '0.85rem' }}>Annuler</button>
              <button onClick={handleIgnorer} style={{ padding: '0.4rem 1rem', borderRadius: '7px', border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Ignorer définitivement</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1.5rem', background: 'var(--bg-card)', borderRadius: '12px', border: `1px solid ${enRetard > 0 ? '#fca5a5' : urgents > 0 ? '#fed7aa' : 'var(--border)'}`, overflow: 'hidden' }}>
        <div style={{ padding: '0.8rem 1rem', borderBottom: open ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
          <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Recyclages à venir</span>
          {total > 0 && (
            <span style={{ background: enRetard > 0 ? '#ef4444' : '#f97316', color: 'white', borderRadius: '12px', padding: '0.1rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>
              {total}
            </span>
          )}
          {enRetard > 0 && <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 700 }}>⚠ {enRetard} en retard</span>}
          {urgents > 0 && <span style={{ fontSize: '0.75rem', color: '#ea580c', fontWeight: 700 }}>⚡ {urgents} urgent{urgents > 1 ? 's' : ''}</span>}
          {total === 0 && !loading && <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Aucun recyclage dans les {jours} prochains jours</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={e => e.stopPropagation()}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Afficher</span>
            <select value={jours} onChange={e => setJours(parseInt(e.target.value))}
              style={{ fontSize: '0.78rem', padding: '0.15rem 0.4rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer' }}>
              <option value={30}>30 j</option>
              <option value={60}>60 j</option>
              <option value={90}>90 j</option>
              <option value={180}>6 mois</option>
              <option value={365}>1 an</option>
            </select>
          </div>
        </div>
        {open && (
          <div style={{ padding: '0.8rem 1rem' }}>
            {loading ? (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Chargement...</div>
            ) : (
              <>
                {/* Section obligatoire */}
                <div style={{ marginBottom: '1.2rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#dc2626', marginBottom: '0.5rem' }}>
                    Recyclages réglementaires ({obligatoires.length})
                  </div>
                  <RecyclageTable rows={obligatoires} />
                </div>
                {/* Section conseillée */}
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#d97706', marginBottom: '0.5rem' }}>
                    Recyclages conseillés ({conseilles.length})
                  </div>
                  <RecyclageTable rows={conseilles} />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Section Stagiaires à planifier ────────────────────────────────────────────
// Stagiaires dont la fiche d'inscription indique qu'aucune des dates proposées ne convient.
// Affichée seulement s'il y en a ; chacun en sort tout seul dès qu'il est inscrit dans une session du devis.

function StagiairesAPlanifierSection() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [data, setData] = useState({ total: 0, formations: [] });
  const [open, setOpen] = useState(true);
  const [erreur, setErreur] = useState('');

  function charger() {
    apiFetch('/dashboard/a-planifier')
      .then(d => setData({ total: d.total || 0, formations: d.formations || [] }))
      .catch(() => {});
  }
  useEffect(charger, []);

  async function retirer(s) {
    if (!window.confirm(`Retirer ${s.prenom} ${s.nom} de la liste « à planifier » ?\n\nÀ utiliser si le client abandonne ou si la demande est traitée autrement. Le stagiaire reste dans la fiche du devis.`)) return;
    setErreur('');
    try {
      await apiFetch('/dashboard/a-planifier/retirer', { method: 'POST', body: JSON.stringify({ fiche_id: s.fiche_id, index: s.index }) });
      charger();
    } catch (e) {
      setErreur(e.message || 'Impossible de retirer ce stagiaire.');
      charger();
    }
  }

  if (data.total === 0) return null;

  const fmt = d => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
  const peutRetirer = hasRole('utilisateur');
  const th = { padding: '0.3rem 0.5rem', textAlign: 'left', fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', whiteSpace: 'nowrap' };

  return (
    <div style={{ marginBottom: '1.5rem', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid #fdba74', overflow: 'hidden' }}>
      <div style={{ padding: '0.8rem 1rem', borderBottom: open ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', flexWrap: 'wrap' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Stagiaires à planifier</span>
        <span style={{ background: '#f97316', color: 'white', borderRadius: '12px', padding: '0.1rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>{data.total}</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
          Aucune des dates proposées ne convenait au client — créez une session, liez-la au devis, puis renvoyez le lien de la fiche.
        </span>
      </div>
      {open && (
        <div style={{ padding: '0.8rem 1rem' }}>
          {erreur && <div style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: '0.6rem' }}>{erreur}</div>}
          {data.formations.map(g => (
            <div key={g.cle} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.86rem', color: 'var(--text-primary)' }}>{g.formation || g.code_formation}</span>
                <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#c2410c' }}>{g.nb} stagiaire{g.nb > 1 ? 's' : ''} en attente</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Stagiaire', 'Client', 'Devis', 'Demande', 'Précisions du client', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {g.stagiaires.map(s => (
                      <tr key={`${s.fiche_id}-${s.index}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{s.nom} {s.prenom}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.client || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>
                          <span style={{ color: 'var(--accent-blue)', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/devis/${s.devis_id}`)}>{s.devis_id}</span>
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmt(s.date_demande)}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', maxWidth: 280, whiteSpace: 'normal' }}>{s.commentaire || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <button className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.76rem', marginRight: '0.4rem' }}
                            onClick={() => navigate(`/devis/${s.devis_id}`)}>Ouvrir le devis</button>
                          {peutRetirer && (
                            <button className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.76rem' }}
                              title="Le client abandonne ou la demande est traitée autrement"
                              onClick={() => retirer(s)}>Retirer</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section Documents Formateurs ──────────────────────────────────────────────

function DocumentsFormateursSection() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    apiFetch('/dashboard/documents-formateurs-expiration')
      .then(d => { setData(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const expires = data.filter(r => r.jours_restants < 0).length;
  const urgents = data.filter(r => r.jours_restants >= 0 && r.jours_restants <= 30).length;
  const total   = data.length;

  function badgeColor(jours) {
    if (jours < 0)   return '#dc2626';
    if (jours <= 30) return '#ea580c';
    return '#d97706';
  }

  return (
    <div style={{ marginBottom: '1.5rem', background: 'var(--bg-card)', borderRadius: '12px', border: `1px solid ${expires > 0 ? '#fca5a5' : urgents > 0 ? '#fed7aa' : 'var(--border)'}`, overflow: 'hidden' }}>
      <div style={{ padding: '0.8rem 1rem', borderBottom: open ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Documents formateurs</span>
        {total > 0 && (
          <span style={{ background: expires > 0 ? '#ef4444' : '#f97316', color: 'white', borderRadius: '12px', padding: '0.1rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>
            {total}
          </span>
        )}
        {expires > 0 && <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 700 }}>⚠ {expires} expiré{expires > 1 ? 's' : ''}</span>}
        {urgents > 0 && <span style={{ fontSize: '0.75rem', color: '#ea580c', fontWeight: 700 }}>⚡ {urgents} urgent{urgents > 1 ? 's' : ''}</span>}
        {total === 0 && !loading && <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Aucun document expirant dans les 60 prochains jours</span>}
      </div>
      {open && (
        <div style={{ padding: '0.8rem 1rem' }}>
          {loading ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Chargement...</div>
          ) : total === 0 ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', padding: '0.5rem 0' }}>
              Aucun document expirant dans les 60 prochains jours.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Formateur', 'Document', 'Échéance', 'Jours'].map(h => (
                      <th key={h} style={{ padding: '0.3rem 0.5rem', textAlign: 'left', fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((r, i) => {
                    const color  = badgeColor(r.jours_restants);
                    const expire = r.jours_restants < 0;
                    return (
                      <tr key={i}
                        style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onClick={() => navigate(`/formateurs/${r.formateur_id}`)}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.nom_formateur || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.type_document || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color, fontWeight: expire ? 700 : 500, whiteSpace: 'nowrap' }}>
                          {r.date_fin_validite ? new Date(r.date_fin_validite).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', fontWeight: 700, color, whiteSpace: 'nowrap' }}>
                          {expire ? `${Math.abs(r.jours_restants)}j expiré ⚠` : `${r.jours_restants}j`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section Réclamations ─────────────────────────────────────────────────────

function ReclamationsSection() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    apiFetch('/dashboard/reclamations').then(d => setData(d)).catch(() => {});
  }, []);

  const stats = data?.stats || {};
  const liste = data?.liste || [];
  const delai = data?.delai_parametre || 10;
  const ouvertes = parseInt(stats.ouvertes || 0, 10);
  const enRetard = parseInt(stats.en_retard || 0, 10);

  return (
    <div style={{ marginBottom: '1.5rem', background: 'var(--bg-card)', borderRadius: '12px', border: `1px solid ${enRetard > 0 ? '#fca5a5' : 'var(--border)'}`, overflow: 'hidden' }}>
      <div style={{ padding: '0.8rem 1rem', borderBottom: open ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Réclamations</span>
        {ouvertes > 0 && (
          <span style={{ background: enRetard > 0 ? '#ef4444' : '#3b82f6', color: 'white', borderRadius: '12px', padding: '0.1rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>
            {ouvertes}
          </span>
        )}
        {enRetard > 0 && <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 700 }}>⚠ {enRetard} en retard</span>}
        <button onClick={e => { e.stopPropagation(); navigate('/reclamations'); }}
          style={{ marginLeft: 'auto', padding: '0.25rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
          Voir tout
        </button>
      </div>
      {open && (
        <div style={{ padding: '0.8rem 1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Ouvertes', val: ouvertes, color: '#3b82f6' },
              { label: `En retard (>${delai}j)`, val: enRetard, color: enRetard > 0 ? '#dc2626' : '#64748b' },
              { label: 'Clôturées', val: stats.cloturees || 0, color: '#16a34a' },
              { label: 'Délai moyen', val: stats.delai_moyen_jours ? `${stats.delai_moyen_jours}j` : '—', color: '#64748b' },
            ].map(k => (
              <div key={k.label} style={{ flex: 1, minWidth: 110, background: 'var(--bg-elevated)', borderRadius: '8px', padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: k.color }}>{k.val}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: '0.1rem' }}>{k.label}</div>
              </div>
            ))}
          </div>
          {liste.length === 0 ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', padding: '0.5rem 0' }}>Aucune réclamation ouverte.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Réf.', 'Date', 'Objet', 'Client', 'Jours'].map(h => (
                    <th key={h} style={{ padding: '0.3rem 0.5rem', textAlign: 'left', fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {liste.map(r => {
                  const tard = Number(r.jours_ecoules) > delai;
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => navigate(`/reclamations/${r.id}`)}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '0.35rem 0.5rem', fontWeight: 700, color: 'var(--accent-blue)' }}>{r.reference}</td>
                      <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{r.date_reclamation ? new Date(r.date_reclamation).toLocaleDateString('fr-FR') : '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.objet || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: 'var(--text-secondary)' }}>{r.nom_client || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', fontWeight: tard ? 700 : 400, color: tard ? '#dc2626' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {r.jours_ecoules != null ? `${r.jours_ecoules}j` : '—'}{tard ? ' ⚠' : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── Dashboard principal ───────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];

export default function Dashboard() {
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [openChiffres, setOpenChiffres] = useState(false);
  const { data, loading } = useApi('/dashboard', { year: selectedYear }, [selectedYear]);

  if (loading || !data) return <Loading />;
  const { stats, kpiAnnee, comparatif, mensuel, topFormations, topClients } = data;
  const years = comparatif.map(c => c.annee);

  return (
    <div className="page">
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble de l'activité formation" actions={<BackButton />} />

      {/* Stagiaires à planifier (aucune date proposée ne convenait) — affiché seulement s'il y en a */}
      <StagiairesAPlanifierSection />

      {/* Tableau d'avancement sessions à venir */}
      <AvancementSessions />

      {/* Sessions à facturer */}
      <AFacturerSessions />

      {/* Recyclages à venir */}
      <RecyclagesSection />

      {/* Documents formateurs expirant */}
      <DocumentsFormateursSection />

      {/* Réclamations */}
      <ReclamationsSection />

      {/* Satisfaction */}
      <SatisfactionSection />

      {/* Chiffres clés — section collapsible */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div
          style={{ padding: '0.8rem 1rem', borderBottom: openChiffres ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', flexWrap: 'wrap' }}
          onClick={() => setOpenChiffres(o => !o)}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', transition: 'transform 0.18s', transform: openChiffres ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
          <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>Chiffres clés</span>
          <select
            value={selectedYear}
            onClick={e => e.stopPropagation()}
            onChange={e => { e.stopPropagation(); setSelectedYear(Number(e.target.value)); if (!openChiffres) setOpenChiffres(true); }}
            style={{ padding: '0.3rem 0.7rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' }}>
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {openChiffres && (
          <div style={{ padding: '1rem 1.2rem' }}>
            {/* Stats globales */}
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <StatCard icon="◎" label="Clients actifs" value={stats.total_clients} accent="accent-blue" />
              <StatCard icon="▦" label="Formations" value={stats.total_formations} accent="accent-violet" />
              <StatCard icon="◑" label="Formateurs" value={stats.total_formateurs} accent="accent-blue" />
              <StatCard icon="◐" label="Sessions à venir" value={stats.sessions_a_venir} accent="accent-amber" />
            </div>

            {/* KPI de l'année sélectionnée */}
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <StatCard icon="▦" label={`Sessions ${kpiAnnee.annee}`} value={kpiAnnee.sessions} accent="accent-emerald" />
              <StatCard icon="◐" label={`Stagiaires ${kpiAnnee.annee}`} value={kpiAnnee.stagiaires} accent="accent-amber" />
              <StatCard icon="€" label={`CA Total ${kpiAnnee.annee}`} value={formatCurrency(kpiAnnee.ca_total)} accent="accent-emerald" />
            </div>

            {/* Comparatif 3 ans */}
            <ComparatifTable comparatif={comparatif} />

            {/* Graphiques multi-années */}
            <div className="dashboard-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="card">
                <h3 className="card-title">Sessions / mois</h3>
                <LineChart mensuel={mensuel} years={years} valueKey="nb_sessions"
                  formatLabel={v => Math.round(v)} />
                <ChartLegend years={years} />
              </div>
              <div className="card">
                <h3 className="card-title">Chiffre d'affaires / mois <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>(en k€)</span></h3>
                <LineChart
                  mensuel={mensuel}
                  years={years}
                  valueKey="ca"
                  valueKeyEstime="ca_estime"
                  formatVal={v => v >= 1000 ? `${Math.round(v / 1000)}k` : Math.round(v)}
                  formatLabel={v => v >= 1000 ? (v / 1000).toFixed(1).replace('.', ',') : Math.round(v)}
                />
                <ChartLegend years={years} />
              </div>
            </div>

            {/* Top formations + top clients */}
            <div className="dashboard-grid">
              <div className="card">
                <h3 className="card-title">Top Formations {selectedYear}</h3>
                <div className="ranking-list">
                  {topFormations.length ? topFormations.map((f, i) => (
                    <div key={i} className="ranking-item">
                      <span className="ranking-pos">{i + 1}</span>
                      <span className="ranking-name">{f.intitule_formation}</span>
                      <span className="ranking-val">{f.nb_sessions} sessions</span>
                    </div>
                  )) : <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune session cette année.</p>}
                </div>
              </div>
              <div className="card">
                <h3 className="card-title">Top Clients {selectedYear}</h3>
                <div className="ranking-list">
                  {topClients.length ? topClients.map((c, i) => (
                    <div key={i} className="ranking-item">
                      <span className="ranking-pos">{i + 1}</span>
                      <span className="ranking-name">{c.client_name}</span>
                      <span className="ranking-val">{formatCurrency(c.ca_total)}</span>
                    </div>
                  )) : <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucun devis validé cette année.</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
